import { Stack, Typography, useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useState } from 'react';

import { Strings } from '@app/constants';
import { StyledImageStack } from '../styles';
import { addCoverImage, deleteAttacmentByID } from '../slice';
import { IImageUrl } from '../types';
import { selectAddCoverImageState, selectDeleteAttachmentState } from '../selector';
import { useRequestState } from '@app/hooks';
import { formatTimeSlots } from '@app/components';
import { AttachmentViewer } from '@app/components/card';
import { Iconify } from '@app/components/iconify';

interface Props {
  imageUrl: IImageUrl[];
}
const AttachmentCard = ({ imageUrl }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState('');

  const handleOpenViewer = (attachment: string) => {
    setIsViewerOpen(true);
    setSelectedAttachment(attachment);
  };

  useRequestState({
    stateSelector: selectAddCoverImageState,
    errorShown: true,
  });

  useRequestState({
    stateSelector: selectDeleteAttachmentState,
    errorShown: true,
  });

  const handleDeleteAttachment = (attachmentId: number) => {
    dispatch(deleteAttacmentByID({ attachmentId }));
  };

  const getFileNameFromUrl = (url: string) => {
    const regex = /\/([^/?#]+)\?/;
    const matches = url.match(regex);
    return matches ? decodeURIComponent(matches[1]) : null;
  };

  const handleMakeCoverImage = (attachmentId: number, isCoverImage: boolean) => {
    dispatch(addCoverImage({ attachmentId, isCoverImage }));
  };

  const handleDownloadAttachment = async (url: any) => {
    try {
      const response = await fetch(url?.attachment, { mode: 'cors' });
      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getFileNameFromUrl(url?.attachment) ?? 'download';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const isVideoFile = (fileName: string | null) => {
    if (!fileName) return false;
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    return extension ? videoExtensions.includes(extension) : false;
  };

  return (
    <>
      <Typography sx={{ fontWeight: 500, color: theme.palette.grey['800'] }} ml={1} mb={1} mt={1.5}>
        {Strings.taskBoard.attachment}
      </Typography>

      {imageUrl?.map((url: IImageUrl, index: number) => (
        <Stack
          sx={{
            flexDirection: 'row',
            width: '100%',
            background: 'none',
            borderRadius: '5px',
          }}
          key={index}
          mb={1.5}
          p={1}
          gap={1}
        >
          <Stack key={index} sx={{ width: '25%', borderRadius: '4px', justifyContent: 'center' }}>
            {(() => {
              const fileName = getFileNameFromUrl(url.attachment);
              if (fileName?.split('.').pop()?.toLowerCase() === 'pdf') {
                return (
                  <Stack
                    sx={{
                      backgroundColor: theme.palette.grey[400],
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: theme.palette.grey[800],
                      }}
                    >
                      {Strings.taskBoard.pdf}
                    </Typography>
                  </Stack>
                );
              } else if (isVideoFile(fileName)) {
                return (
                  <Stack
                    onClick={() => {
                      handleOpenViewer(url?.attachment);
                    }}
                    sx={{
                      height: '100%',
                      minHeight: '50px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <video
                      src={url.attachment}
                      style={{
                        width: '100%',
                        height: '50px',
                        objectFit: 'cover',
                      }}
                    />
                    <Stack
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Iconify
                        icon="mdi:play-circle"
                        sx={{
                          width: 40,
                          height: 40,
                          color: 'white',
                        }}
                      />
                    </Stack>
                  </Stack>
                );
              } else {
                return (
                  <StyledImageStack
                    onClick={() => {
                      handleOpenViewer(url?.attachment);
                    }}
                    url={url?.attachment}
                    sx={{ cursor: 'pointer' }}
                    height={'100%'}
                    width={'100%'}
                  />
                );
              }
            })()}
          </Stack>

          <Stack sx={{ width: '100%' }}>
            <Stack>
              <Typography sx={{ fontSize: '15px', fontWeight: 550 }}>
                {getFileNameFromUrl(url?.attachment)}
              </Typography>
            </Stack>
            <Stack>
              <Typography
                sx={{ fontSize: '11px', color: theme.palette.common.black, fontWeight: 500 }}
              >
                {formatTimeSlots([url?.createdAt])}
              </Typography>
            </Stack>
            <Stack
              sx={{
                flexDirection: 'row',
              }}
              gap={2}
            >
              <Stack
                onClick={() => {
                  void handleDownloadAttachment(url);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <Typography
                  sx={{
                    textDecoration: 'underline',
                    fontSize: '13px',
                    cursor: 'pointer',
                    color: theme.palette.taskboard.deepPurple,
                  }}
                >
                  {Strings.taskBoard.download}
                </Typography>
              </Stack>
              <Stack
                onClick={() => {
                  handleDeleteAttachment(url?.id);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <Typography
                  sx={{
                    textDecoration: 'underline',
                    fontSize: '13px',
                    color: theme.palette.taskboard.deepPurple,
                  }}
                >
                  {Strings.taskBoard.delete}
                </Typography>
              </Stack>
              {!isVideoFile(getFileNameFromUrl(url?.attachment)) && (
                <Stack
                  sx={{ display: 'flex', flexDirection: 'row', cursor: 'pointer' }}
                  onClick={() => {
                    handleMakeCoverImage(url?.id, !url?.isCoverImage);
                  }}
                >
                  <Typography
                    sx={{
                      textDecoration: 'underline',
                      fontSize: '13px',
                      color: theme.palette.taskboard.deepPurple,
                    }}
                  >
                    {url?.isCoverImage
                      ? Strings.taskBoard.removeCover
                      : Strings.taskBoard.makeCover}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      ))}
      <AttachmentViewer
        titleName={Strings.taskBoard.attachment}
        open={isViewerOpen}
        resume={selectedAttachment}
        onClose={() => {
          setIsViewerOpen(false);
        }}
      />
    </>
  );
};

export { AttachmentCard };
